import { CheckCircle2 } from "lucide-react";
import type { BatteryOutlook, Reading } from "@spf/contracts";
import { Card } from "../Card.js";
import { formatUnit } from "../../lib/format.js";
import { counters } from "../../lib/counters.js";

interface Props {
  reading: Reading;
  outlook: BatteryOutlook | null;
}

/**
 * The design's state-of-charge card: the headline row, the capacity bar with
 * the reserve marker standing in it, and the tiled measurement band below.
 *
 * **The bar's reserve line is the configured floor, not a slider the reader
 * moved.** In the design it is local state initialised to 20 and means
 * nothing; here it is `batteryReserveSocPct` from the instance settings, which
 * is the same number the server measures runtime down to. A marker that did
 * not agree with the arithmetic would be worse than no marker.
 *
 * The design's band below is sixteen per-cell voltages from a LiFePO4 BMS.
 * This inverter reports pack terminal voltage and nothing per cell -- there is
 * no cell balance to show and no later slice can add one, because the hardware
 * does not send it. The tiles keep their shape and carry the pack measurements
 * that do exist.
 */
export function ChargeCard({ reading, outlook }: Props) {
  const soc = reading.socPct;
  const reserve = outlook?.reserveSocPct ?? null;

  return (
    <Card title="State of charge and usable capacity" className="lg:col-span-2">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-md text-xs text-ink-muted">
          The percentage is the inverter&rsquo;s own. Everything measured in kilowatt-hours is that
          percentage against the capacity configured for this instance.
        </p>
        <span className="shrink-0 rounded-full bg-line px-3 py-1 text-xs font-semibold text-ink">
          {statusOf(reading.batteryPowerW)}
        </span>
      </div>

      <div className="my-6">
        <div className="tabular mb-2 flex items-center justify-between text-sm">
          <span className="text-ink-muted">
            {outlook === null
              ? "Capacity not configured"
              : `Usable ${formatUnit(outlook.usableKwh, "kWh")} of ${formatUnit(outlook.capacityKwh, "kWh")}`}
          </span>
          <span className="font-bold text-ink">{formatUnit(soc, "%")}</span>
        </div>

        {/* The bar repeats what the row above already says in words, so it is
            hidden from readers rather than described twice. */}
        <div
          aria-hidden="true"
          className="relative flex h-8 items-center rounded-full border border-line bg-line/60 p-1"
        >
          <div
            className="h-full rounded-full bg-accent transition-all duration-700 ease-out"
            style={{ width: `${String(Math.max(0, Math.min(100, soc)))}%` }}
          />
          {reserve !== null && (
            <div
              className="absolute bottom-0 top-0 z-10 w-0.5 bg-danger"
              style={{ left: `${String(reserve)}%` }}
            />
          )}
        </div>

        <div className="tabular mt-3 flex items-center justify-between text-xs text-ink-muted">
          <span>0% empty</span>
          {reserve !== null && (
            <span className="text-danger-ink">{`Reserve floor ${String(reserve)}%`}</span>
          )}
          <span>100% full</span>
        </div>
      </div>

      <div className="border-t border-line pt-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-bold text-ink">Pack measurements</h4>
          <span className="flex items-center gap-1 text-xs text-accent-2-ink">
            <CheckCircle2 size={13} aria-hidden="true" /> Reported by the inverter
          </span>
        </div>

        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Tile label="Terminal voltage" value={formatUnit(reading.batteryVoltageV, "V")} />
          <Tile label="Current" value={formatUnit(reading.batteryCurrentA, "A")} />
          <Tile label="Power" value={formatUnit(Math.abs(reading.batteryPowerW), "W", true)} />
          {packCounters(reading).map((counter) => (
            <Tile
              key={counter.label}
              label={counter.label}
              value={formatUnit(counter.value, "kWh")}
            />
          ))}
          <Tile label="DC-DC temperature" value={formatUnit(reading.dcdcTempC, "°C")} />
        </dl>

        <p className="mt-3 text-xs text-ink-muted">
          Per-cell balance is not shown because this inverter does not report it. It sends one pack
          voltage, not sixteen cell voltages, so there is nothing to balance against.
        </p>
      </div>
    </Card>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-line bg-line/30 p-2 text-center">
      <dt className="block text-[10px] text-ink-muted">{label}</dt>
      <dd className="tabular text-xs font-bold text-ink">{value}</dd>
    </div>
  );
}

/**
 * Words rather than a signed number. The protocol's sign convention --
 * negative is charging -- is a fact about the wire and not something a reader
 * should have to hold in their head.
 */
function statusOf(batteryPowerW: number): string {
  const magnitude = formatUnit(Math.abs(batteryPowerW), "W", true);
  if (batteryPowerW < 0) return `Charging at ${magnitude}`;
  if (batteryPowerW > 0) return `Discharging at ${magnitude}`;
  return "Idle";
}

/**
 * The pack's two counters, today and lifetime. The third counter is house load
 * rather than anything the pack did, so it belongs to the Live view, not here.
 */
function packCounters(reading: Reading) {
  return [
    ...counters(reading, "today")
      .slice(0, 2)
      .map((counter) => ({ ...counter, label: `${counter.label} today` })),
    ...counters(reading, "total")
      .slice(0, 2)
      .map((counter) => ({ ...counter, label: `${counter.label}, lifetime` })),
  ];
}
